import { ViewIds } from '@ardoq/api-types';
import { mergeWith, pick } from 'lodash';
import {
  TypedEntries,
  ViewpointModeViewKey,
  WorkspaceModeViewKey,
} from './types';
import {
  VIEWPOINT_MODE_VIEW_DEFINITIONS,
  WORKSPACE_MODE_VIEW_DEFINITIONS,
} from 'views/metaInfoConsts';
import type { ModeViews } from 'views/modeViews$';

/**
 * This won't work if the T is a Partial type, since all properties in a Partial type are optional.
 */
export const typedEntries = <T extends Record<string, unknown>>(
  obj: T
): TypedEntries<T> => Object.entries(obj) as TypedEntries<T>;

/**
 * Sanitizes a single view's settings by only keeping keys that exist in the default settings.
 * This ensures that settings updates don't introduce keys that shouldn't exist in certain
 * environments (e.g., workspace-only settings like `highlightDisconnectedComponents` in viewpoint mode).
 *
 * @template T - The type of the view settings object
 * @param settings - Partial settings object that may contain invalid keys
 * @param defaultSettings - The complete default settings object defining valid keys
 * @returns A complete settings object with only valid keys from the input, merged with defaults
 */
export const sanitizeSingleViewSettings = <T extends Record<string, any>>(
  settings: Partial<T>,
  defaultSettings: T
): T => {
  const sanitized = pick(settings, Object.keys(defaultSettings));
  return { ...defaultSettings, ...sanitized };
};

/**
 * Picks the keys from `maybeSavedContextSettings` that match the keys of
 * `defaultSavedContextSettings`, and returns a new object with those keys.
 * If a key is not present in `maybeSavedContextSettings`, the default value
 * from `defaultSavedContextSettings` is used.
 *
 * We spread the saved settings on top of the default settings, to maintain
 * the shape in case the saved settings are incomplete.
 */
export const mergeAndSanitizeGlobalSettings = <
  T extends Record<string, Record<keyof T[keyof T], unknown>>,
>(
  source: Record<string, unknown>,
  target: T
): T => {
  const viewKeys = Object.keys(target);
  return Object.fromEntries(
    viewKeys.map(key => {
      const settingsToMerge = source[key];
      const settingsToMergeInto = target[key];

      if (!settingsToMerge || typeof settingsToMerge !== 'object') {
        return [key, settingsToMergeInto];
      }

      const sanitizedSettings = sanitizeSingleViewSettings(
        settingsToMerge,
        settingsToMergeInto
      );

      return [key, sanitizedSettings];
    })
  ) as T;
};

type ViewSettingsUpdateRelevantForInstanceArgs = {
  subscriberViewId: ViewIds;
  subscriberViewInstanceId?: string;
  updatedViewId?: ViewIds;
  updatedViewInstanceId?: string;
};
export const isViewSettingsUpdateRelevantForInstance = ({
  subscriberViewId,
  subscriberViewInstanceId,
  updatedViewId,
  updatedViewInstanceId,
}: ViewSettingsUpdateRelevantForInstanceArgs) => {
  if (!updatedViewId) {
    // If the update has no view type specified, the update is for all views
    return true;
  }

  if (updatedViewId !== subscriberViewId) {
    return false;
  }

  if (!subscriberViewInstanceId || !updatedViewInstanceId) {
    /**
     * If the viewInstanceId is not specified by subscriber, we are interested
     * in all updates for the view type.
     *
     * If it is not specified in the update, we are broadcasting the update to
     * all subscribers of the view type.
     */
    return true;
  }

  /**
   * If the view instance ids are specified, the update comes from the view
   * instance and should only concern the specified view instance.
   */
  return updatedViewInstanceId === subscriberViewInstanceId;
};

export const isViewpointModeView = (
  maybeViewId: string
): maybeViewId is ViewpointModeViewKey =>
  VIEWPOINT_MODE_VIEW_DEFINITIONS.some(({ id }) => id === maybeViewId);
export const isWorkspaceModeView = (
  maybeViewId: string
): maybeViewId is WorkspaceModeViewKey =>
  WORKSPACE_MODE_VIEW_DEFINITIONS.some(({ id }) => id === maybeViewId);

export const toCurrentModeViews = (
  isViewpointMode: boolean,
  viewpointModeViews: ModeViews<ViewIds>,
  workspaceModeViews: ModeViews<ViewIds>
): ModeViews<ViewIds> =>
  isViewpointMode ? viewpointModeViews : workspaceModeViews;

/**
 * Merge the view settings properties on the top level, but do not deep-merge
 * the property values, there, the saved setting wins over the current setting.
 */
export const mergeViewSettings = <T, U>(
  currentSettings: Partial<T>,
  savedSettings: Partial<U>
) =>
  mergeWith(
    currentSettings,
    savedSettings,
    (targetValue, srcValue, key, targetObj, sourceObj) => {
      // if one of the objects missing the key, take what is available
      if (!(key in targetObj) || !(key in sourceObj)) {
        return srcValue ?? targetValue;
      }

      // Both objects have the key. If it is a viewId, merge the keys of the view settings,
      // replacing the values from currentSettings with those from savedSettings.
      if (!Object.values(ViewIds).includes(key as ViewIds)) {
        return srcValue;
      }

      // The key is a viewId, merge the view settings keys (let the method handle the merge)
      return undefined;
    }
  );
